import React, { useState } from 'react';
import { Plus, Pencil, Trash2, BriefcaseBusiness } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateTalentDiagnosticsBenchmark,
  useDeleteTalentDiagnosticsBenchmark,
  useTalentDiagnosticsBenchmarks,
  useUpdateTalentDiagnosticsBenchmark,
} from '../../../hooks/useTalentDiagnostics';

interface Props {
  definitionId: string;
}

export const TalentDiagnosticsBenchmarksTab: React.FC<Props> = ({ definitionId }) => {
  const { data: benchmarks = [], isLoading } = useTalentDiagnosticsBenchmarks(definitionId);
  const createMutation = useCreateTalentDiagnosticsBenchmark(definitionId);
  const updateMutation = useUpdateTalentDiagnosticsBenchmark(definitionId);
  const deleteMutation = useDeleteTalentDiagnosticsBenchmark(definitionId);

  const [form, setForm] = useState({
    name: '',
    minScore: '0',
    maxScore: '100',
    maturityLevel: '',
    priority: '',
    summary: '',
    focusAreas: '',
    visualIndicator: ''
  });
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setForm({
      name: '',
      minScore: '0',
      maxScore: '100',
      maturityLevel: '',
      priority: '',
      summary: '',
      focusAreas: '',
      visualIndicator: '',
    });
    setEditingId(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!definitionId) {
      toast.error('Enter a definition ID first');
      return;
    }

    if (!form.name.trim()) {
      toast.error('Benchmark name is required');
      return;
    }

    const recommendationJson = JSON.stringify({
      maturityLevel: form.maturityLevel.trim(),
      priority: form.priority.trim(),
      summary: form.summary.trim(),
      visualIndicator: form.visualIndicator,
      focusAreas: form.focusAreas
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean),
    });

    const payload = {
      id: editingId ?? undefined,
      diagnosticDefinitionId: definitionId,
      name: form.name.trim(),
      minScore: Number(form.minScore || 0),
      maxScore: Number(form.maxScore || 0),
      visualIndicator: form.visualIndicator,
      recommendationJson,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }

    resetForm();
  };

  const startEdit = (
    benchmark: {
      id?: string;
      name?: string;
      minScore?: number;
      maxScore?: number;
      visualIndicator?: string;
      recommendationJson?: string;
    }
  ) => {
    let maturityLevel = '';
    let priority = '';
    let summary = '';
    let focusAreas = '';
    let visualIndicator = '';

    try {
      const recommendation = JSON.parse(
        benchmark.recommendationJson || '{}'
      );

      maturityLevel = recommendation.maturityLevel ?? '';
      priority = recommendation.priority ?? '';
      summary = recommendation.summary ?? '';
      visualIndicator = benchmark.visualIndicator ?? '';

      focusAreas = Array.isArray(recommendation.focusAreas)
        ? recommendation.focusAreas.join('\n')
        : '';
    } catch (error) {
      console.error(error);
    }

    setEditingId(benchmark.id ?? null);

    setForm({
      name: benchmark.name ?? '',
      minScore: String(benchmark.minScore ?? 0),
      maxScore: String(benchmark.maxScore ?? 100),
      maturityLevel,
      priority,
      summary,
      focusAreas,
      visualIndicator,
    });
  };

  const handleDelete = (id?: string) => {
    if (!id) return;
    if (window.confirm('Delete this benchmark?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <div className="flex items-center gap-2">
          <BriefcaseBusiness size={18} className="text-strivre-burgundy" />
          <h4 className="text-base font-semibold text-slate-900">Manage benchmarks</h4>
        </div>
        <p className="mt-1 text-sm text-slate-500">Create score-based benchmarks to compare selected diagnostic outcomes.</p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Benchmark name</label>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                placeholder="e.g. Leadership Score"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Maturity Level
              </label>

              <input
                value={form.maturityLevel}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    maturityLevel: e.target.value,
                  }))
                }
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-slate-900"
                placeholder="At Risk"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Priority
              </label>

              <input
                value={form.priority}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    priority: e.target.value,
                  }))
                }
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-slate-900"
                placeholder="Critical"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Visual Indicator
              </label>

              <select
                value={form.visualIndicator}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    visualIndicator: e.target.value,
                  }))
                }
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-slate-900"
              >
                <option value="">Select indicator</option>
                <option value="excellent">Excellent</option>
                <option value="neutral">Neutral</option>
                <option value="poor">Poor</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Focus Areas
              </label>

              <textarea
                value={form.focusAreas}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    focusAreas: e.target.value,
                  }))
                }
                rows={6}
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-3 text-slate-900"
                placeholder={`One focus area per line

              Process Stabilization
              Workforce Planning
              Candidate Experience
              Recruitment Performance Visibility`}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Summary
              </label>

              <textarea
                value={form.summary}
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    summary: e.target.value,
                  }))
                }
                rows={4}
                className="w-full rounded-xl border border-strivre-lightnavy px-4 py-3 text-slate-900"
                placeholder="Recruitment performance is significantly limiting organizational growth and talent acquisition outcomes."
              />
            </div>
            {/* <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Recommendation JSON</label>
              <input
                value={form.recommendationJson}
                onChange={(event) => setForm((current) => ({ ...current, recommendationJson: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                placeholder="Optional JSON payload"
              />
            </div> */}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Min score</label>
              <input
                type="number"
                value={form.minScore}
                onChange={(event) => setForm((current) => ({ ...current, minScore: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Max score</label>
              <input
                type="number"
                value={form.maxScore}
                onChange={(event) => setForm((current) => ({ ...current, maxScore: event.target.value }))}
                className="w-full rounded-xl border border-strivre-lightnavy bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>
          </div>
          <div className="rounded-2xl border border-strivre-lightnavy bg-slate-50 p-5">
            <h4 className="font-semibold text-slate-900">
              Recommendation Preview
            </h4>

            {form.maturityLevel && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Maturity Level
                </p>
                <p className="text-slate-600">
                  {form.maturityLevel}
                </p>
              </div>
            )}

            {form.priority && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Priority
                </p>
                <p className="text-slate-600">
                  {form.priority}
                </p>
              </div>
            )}

            {form.summary && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Summary
                </p>
                <p className="text-slate-600">
                  {form.summary}
                </p>
              </div>
            )}

            {form.focusAreas && (
              <div className="mt-4">
                <p className="font-medium text-slate-900">
                  Focus Areas
                </p>

                <ul className="mt-2 list-disc pl-5 text-slate-600 space-y-1">
                  {form.focusAreas
                    .split('\n')
                    .filter(Boolean)
                    .map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" className="flex items-center gap-2 rounded-xl bg-strivre-burgundy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-900">
              <Plus size={16} />
              {editingId ? 'Save benchmark' : 'Create benchmark'}
            </button>
            {editingId ? (
              <button type="button" onClick={resetForm} className="rounded-xl border border-strivre-lightnavy px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5 text-left">
        <h4 className="text-base font-semibold text-slate-900">Current benchmarks</h4>
        <p className="text-sm text-slate-500">Use these thresholds to rank diagnostic results.</p>

        {isLoading ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            Loading benchmarks...
          </div>
        ) : benchmarks.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-strivre-lightnavy bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            No benchmarks found for this definition yet.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {benchmarks.map((benchmark) => (
              <div key={benchmark.id ?? `${benchmark.name}-${benchmark.minScore}-${benchmark.maxScore}`} className="flex items-center justify-between rounded-2xl border border-strivre-lightnavy bg-slate-50/60 p-4">
                <div>
                  <p className="font-semibold text-slate-900">{benchmark.name || 'Untitled benchmark'}</p>
                  <p className="text-sm text-slate-500">Score range: {benchmark.minScore ?? 0} - {benchmark.maxScore ?? 100}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => startEdit(benchmark)} className="rounded-xl border border-strivre-lightnavy bg-white p-2 text-slate-600 transition hover:bg-strivre-softgrey">
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(benchmark.id)} className="rounded-xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
